import { CSSProperties, ReactNode, useEffect, useRef, useState } from "react";

export type RevealAnimation = "fadeInUp" | "fadeInLeft" | "fadeInRight" | "flipInX";

interface RevealProps {
  animateIn: RevealAnimation;
  /** Ritardo in ms prima che parta l'animazione, come nella vecchia API. */
  delay?: number;
  style?: CSSProperties;
  className?: string;
  children: ReactNode;
}

/**
 * Un solo IntersectionObserver condiviso da tutte le istanze.
 *
 * Sostituisce react-animate-on-scroll, che agganciava un listener di scroll per
 * ogni istanza e leggeva offsetTop a ogni evento: con ~18 istanze in pagina
 * Lighthouse misurava 55-94ms di forced reflow. L'observer non tocca il layout.
 */
let observer: IntersectionObserver | null = null;
const callbacks = new Map<Element, () => void>();

function getObserver(): IntersectionObserver {
  if (!observer) {
    observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const callback = callbacks.get(entry.target);
          if (!callback) return;
          // Il reveal è one-shot: smetto di osservare appena è partito.
          callbacks.delete(entry.target);
          observer?.unobserve(entry.target);
          callback();
        });
      },
      { rootMargin: "0px 0px -10% 0px", threshold: 0.05 }
    );
  }
  return observer;
}

export function Reveal({ animateIn, delay = 0, style, className, children }: RevealProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;

    // Senza IntersectionObserver mostro subito invece di lasciare il vuoto.
    if (typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }

    callbacks.set(node, () => setIsVisible(true));
    getObserver().observe(node);

    return () => {
      callbacks.delete(node);
      observer?.unobserve(node);
    };
  }, []);

  const classes = ["reveal"];
  if (isVisible) classes.push("reveal--in", `reveal--${animateIn}`);
  if (className) classes.push(className);

  return (
    <div
      ref={ref}
      className={classes.join(" ")}
      style={delay ? { ...style, animationDelay: `${delay}ms` } : style}
    >
      {children}
    </div>
  );
}
